import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Copy, Check, Share2 } from 'lucide-react';

const ShareModal = ({ isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = window.location.origin;

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareWhatsApp = () => {
    const message = encodeURIComponent(`Join me on Baad.tv - Track your favourite TV shows and share reviews with your flock! 🐑 ${shareUrl}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const handleShareTwitter = () => {
    const message = encodeURIComponent('Join me on Baad.tv - Track your favourite TV shows! 🐑');
    window.open(`https://twitter.com/intent/tweet?text=${message}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const handleShareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent('Join me on Baad.tv!');
    const body = encodeURIComponent(`Hey!\n\nI've been using Baad.tv to track my TV shows and share reviews with friends. You should check it out!\n\n${shareUrl}\n\nJoin the flock! 🐑`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="share-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="modal-title">
            <Share2 size={24} />
            Invite Friends to Baad.tv
          </DialogTitle>
        </DialogHeader>

        <div className="share-content">
          <p className="share-description">
            Share Baad.tv with your friends and build your flock! 🐑
          </p>

          <div className="share-link-section">
            <label>Share this link:</label>
            <div className="link-input-group">
              <Input 
                value={shareUrl} 
                readOnly 
                className="share-input"
              />
              <Button onClick={handleCopy} className="copy-btn">
                {copied ? (
                  <>
                    <Check size={18} />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={18} />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="share-buttons-section">
            <label>Or share via:</label>
            <div className="share-buttons">
              <Button onClick={handleShareWhatsApp} className="share-btn whatsapp">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </Button>

              <Button onClick={handleShareTwitter} className="share-btn twitter">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
                Twitter
              </Button>

              <Button onClick={handleShareFacebook} className="share-btn facebook">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </Button>

              <Button onClick={handleShareEmail} className="share-btn email">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                Email
              </Button>
            </div>
          </div>
        </div>

        <style jsx>{`
          .share-modal {
            background: #1a1a1a;
            color: #ffffff;
            border: 1px solid #333333;
            max-width: 500px;
          }

          .share-modal :global(button[class*="absolute"]) {
            border: none !important;
            background: transparent !important;
            box-shadow: none !important;
          }

          .share-modal :global(button[class*="absolute"]:hover) {
            color: #ffffff !important;
            background: transparent !important;
          }

          .share-modal :global(button[class*="absolute"]:focus) {
            border: none !important;
            box-shadow: none !important;
            outline: none !important;
          }

          .modal-title {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            color: #E62F88;
          }

          .share-content {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
            padding: 1rem 0;
          }

          .share-description {
            color: #b0b0b0;
            line-height: 1.5;
            margin: 0;
          }

          .share-link-section,
          .share-buttons-section {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
          }

          .share-link-section label,
          .share-buttons-section label {
            color: #ffffff;
            font-weight: 500;
          }

          .link-input-group {
            display: flex;
            gap: 0.5rem;
          }

          .share-input {
            flex: 1;
            background: #000000 !important;
            color: #ffffff !important;
            border: 1px solid #333333 !important;
          }

          .copy-btn {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            background: #E62F88 !important;
            color: #000000 !important;
            white-space: nowrap;
          }

          .copy-btn:hover {
            background: #e6b800 !important;
          }

          .share-buttons {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 0.75rem;
          }

          .share-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            padding: 0.75rem 1rem;
            font-weight: 500;
          }

          .share-btn.whatsapp {
            background: #25D366;
            color: #ffffff;
          }

          .share-btn.whatsapp:hover {
            background: #20BA5A;
          }

          .share-btn.twitter {
            background: #1DA1F2;
            color: #ffffff;
          }

          .share-btn.twitter:hover {
            background: #1A8CD8;
          }

          .share-btn.facebook {
            background: #1877F2;
            color: #ffffff;
          }

          .share-btn.facebook:hover {
            background: #166FE5;
          }

          .share-btn.email {
            background: #666666;
            color: #ffffff;
          }

          .share-btn.email:hover {
            background: #555555;
          }

          @media (max-width: 768px) {
            .share-modal {
              max-width: 100vw;
              width: 100vw;
              height: 100vh;
              margin: 0;
              border-radius: 0;
            }

            .link-input-group {
              flex-direction: column;
            }

            .copy-btn {
              width: 100%;
              justify-content: center;
            }

            .share-buttons {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
